/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { SelectInput, mixins, mobileUtils, colors } from '@blend/consumer-components';

import { useVideoContext } from '../../../../contexts/videoProvider/videoContext';
import { useVideoInputDevices } from '../../../../hooks/useDevice';
import {
  DEFAULT_VIDEO_CONSTRAINTS,
  DEFAULT_MOBILE_VIDEO_CONSTRAINTS,
  SELECTED_VIDEO_INPUT_KEY,
} from '../../../../definitions/devices';
import { LocalPreview } from '@components/videoProvider/LocalPreview/LocalPreview';
import { diagnostics } from '@utils/diagnostics';

type SelectInputValue = any;

export namespace VideoInputDevice {
  export interface Props extends React.HTMLAttributes<HTMLSelectElement> {
    name: string;
    onChange(value: SelectInputValue | null): void;
  }
}

const styles = StyleSheet.create({
  videoPlaceHolder: {
    width: '100%',
    height: 212,
    backgroundColor: colors.catalystLight03,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoWrapper: {
    ...mixins.margin(0, 0, 16, 0),
  },
});

export const VideoInputDevice: React.FC<VideoInputDevice.Props> = ({ name, onChange }) => {
  const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);

  const { localTracks, videoProvider } = useVideoContext();
  const videoInputDevices = useVideoInputDevices(localTracks);
  const [videoDeviceId, setVideoDeviceId] = useState<string | undefined>(selectedVideoDeviceId || undefined);

  const localVideoTrack = localTracks.find(track => track.kind === 'video') as VideoProvider.ILocalVideoTrack;

  useEffect(() => {
    if (!videoDeviceId && !!videoInputDevices[0]) {
      window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, videoInputDevices[0].deviceId);
      setVideoDeviceId(videoInputDevices[0].deviceId);
    }
  }, [videoInputDevices]);

  useEffect(() => {
    onChange(videoDeviceId);
  }, [videoDeviceId]);

  const replaceTrack = useCallback(
    async newDeviceId => {
      setVideoDeviceId(newDeviceId);
      window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, newDeviceId);
      const constraints = mobileUtils.isMobileDevice() ? DEFAULT_MOBILE_VIDEO_CONSTRAINTS : DEFAULT_VIDEO_CONSTRAINTS;
      try {
        // TODO: (ZOOMSDK) See if deviceId.exact is required further down.
        await localVideoTrack.restart({
          ...(constraints as {}),
          deviceId: {
            exact: newDeviceId,
          },
        });
        /**
         * Calling streamSwitchCamera() is required for zoom since we have 2 entities, localTrack and streamTrack.
         * One can be individually changed it without the other. So adding this code here will trigger
         * video switching on stream. For Twilio it will run a void function since they manage everything from localTracks.
         */
        videoProvider.streamSwitchCamera(newDeviceId);
      } catch (error) {
        /**
         * TODO: (ZOOMSDK) error on localTrack.switchCamera
         * To reproduce this error you need a camera that it's listed as a device but it has no videoReady.
         * You can do this if you install an app to computer that can use your phones camera, so the software should list the virtual camera
         * as an available but if your phone it's not connected localTrack.switchCamera shouldn't work properly in this scenario.
         */
        const zoomErrorMessage = 'VideoNotStartedError';
        if (error instanceof Error && error.message === zoomErrorMessage) {
          localVideoTrack.isVideoStarted = false;
          localVideoTrack.videoStream = undefined;
          localVideoTrack.start && (await localVideoTrack?.start(localVideoTrack?.videoParams?.videoDOMElement));
        }
        diagnostics.error(error as Error);
      }
    },
    [localVideoTrack],
  );

  const handleChange = useCallback(
    e => {
      const newDeviceId = e.target.value;
      replaceTrack(newDeviceId);
    },
    [localVideoTrack],
  );

  const choices = useMemo(
    () =>
      videoInputDevices.map(device => {
        return { value: device.deviceId, text: device.label };
      }),
    [videoInputDevices],
  );
  return (
    <>
      <div className={css(styles.videoWrapper)}>
        {!!localVideoTrack ? <LocalPreview /> : <div className={css(styles.videoPlaceHolder)} />}
      </div>
      <SelectInput
        value={videoDeviceId}
        id={name}
        choices={choices}
        onChange={handleChange}
        color={colors.catalystLight40}
        aria-label={'Select video device'}
      />
    </>
  );
};
